import { useCallback, useEffect, useRef, useState } from "react";
import { _post } from "../../../libraries/fetch";
import { ensureProductionApiToken } from "../../../libraries/auth";
import { getSaleOrderReportURL } from "../../../libraries/apiUrl";

const pad2 = (n) => String(n).padStart(2, "0");

export function getMonthDateRange(month, year) {
  const m = Number(month);
  const y = Number(year);
  const lastDay = new Date(y, m, 0).getDate();
  return {
    from: `${y}-${pad2(m)}-01`,
    to: `${y}-${pad2(m)}-${pad2(lastDay)}`
  };
}

export function normalizeSaleOrders(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.response?.data)) return response.response.data;
  if (Array.isArray(response?.response)) return response.response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.orders)) return response.orders;
  return [];
}

export function sumPosSalesTotal(orders) {
  let totalDiscount = 0;
  let totalSale = 0;
  let totalTax = 0;

  for (const order of orders) {
    totalDiscount += Number(order?.discount) || 0;
    totalSale += Number(order?.subTotal ?? order?.total ?? order?.amount) || 0;
    totalTax += Number(order?.vatTax ?? order?.tax) || 0;
  }

  return (totalSale - totalDiscount) + totalTax;
}

function isEmptyReportError(response) {
  const status = response?.status;
  const message = String(response?.message || response?.error || "");
  return (
    status === 404 ||
    /not found|no (sale )?order|no records|empty/i.test(message)
  );
}

export async function fetchPosSaleOrders(branchId, month, year) {
  const resolvedBranchId =
    typeof branchId === "object" && branchId !== null
      ? String(branchId._id || branchId.id || "").trim()
      : String(branchId || "").trim();

  if (!resolvedBranchId) {
    return { orders: [], total: 0, from: "", to: "", error: "Waiting for branch..." };
  }

  await ensureProductionApiToken();
  const { from, to } = getMonthDateRange(month, year);
  const response = await _post(getSaleOrderReportURL(resolvedBranchId), {
    from,
    to,
    saleType: "All",
    conductedBy: "All"
  });

  if (response?.success === false) {
    if (isEmptyReportError(response)) {
      return { orders: [], total: 0, from, to, error: "" };
    }

    return {
      orders: [],
      total: 0,
      from,
      to,
      error: response?.message || "Failed to load POS sales"
    };
  }

  const orders = normalizeSaleOrders(response);
  return {
    orders,
    total: sumPosSalesTotal(orders),
    from,
    to,
    error: ""
  };
}

const formatCurrency = (num) => {
  if (num === null || num === undefined) return "—";
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

function SalesOrders({
  branchId,
  month,
  year,
  onTotalChange,
  showList = true
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [range, setRange] = useState({ from: "", to: "" });
  const onTotalChangeRef = useRef(onTotalChange);

  useEffect(() => {
    onTotalChangeRef.current = onTotalChange;
  }, [onTotalChange]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await fetchPosSaleOrders(branchId, month, year);
      setOrders(result.orders);
      setTotal(result.total);
      setRange({ from: result.from, to: result.to });
      setError(result.error || "");
      if (typeof onTotalChangeRef.current === "function") {
        onTotalChangeRef.current(result.total, result.orders, result);
      }
    } catch (err) {
      const message = err?.message || "Failed to load POS sales";
      setOrders([]);
      setTotal(0);
      setError(message);
      if (typeof onTotalChangeRef.current === "function") {
        onTotalChangeRef.current(0, [], { error: message });
      }
    } finally {
      setLoading(false);
    }
  }, [branchId, month, year]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  if (!showList) return null;

  return (
    <div className="admin-sales-orders">
      <div className="admin-sales-orders-header">
        <div>
          <h3>POS Sales Orders</h3>
          <p>
            {range.from && range.to
              ? `${range.from} → ${range.to}`
              : "Selected month"}
            {" · "}
            {orders.length} order{orders.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="admin-sales-orders-total-card">
          <span>Total POS Sales</span>
          <strong>{loading ? "…" : formatCurrency(total)}</strong>
        </div>
      </div>

      {loading && <div className="admin-dashboard-loading">Loading POS sales…</div>}
      {!loading && error && <div className="admin-dashboard-empty">{error}</div>}
      {!loading && !error && orders.length === 0 && (
        <div className="admin-dashboard-empty">No POS sales found for this month.</div>
      )}

      {!loading && !error && orders.length > 0 && (
        <div className="admin-sales-orders-table-wrap">
          <table className="admin-sales-orders-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Sold To</th>
                <th>Payment</th>
                <th>Subtotal</th>
                <th>Discount</th>
                <th>Tax</th>
                <th>Total</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, index) => {
                const discount = Number(order?.discount) || 0;
                const subTotal = Number(order?.subTotal ?? order?.total ?? order?.amount) || 0;
                const tax = Number(order?.vatTax ?? order?.tax) || 0;
                const lineTotal = (subTotal - discount) + tax;
                return (
                  <tr key={order._id || order.invoiceNumber || `pos-order-${index}`}>
                    <td>{order.invoiceNumber || "—"}</td>
                    <td>{formatDate(order.invoiceDate)}</td>
                    <td>{order.soldTo || "—"}</td>
                    <td>{order.modeOfPayment || order.paymentMode || "—"}</td>
                    <td>{formatCurrency(subTotal)}</td>
                    <td>{formatCurrency(discount)}</td>
                    <td>{formatCurrency(tax)}</td>
                    <td>{formatCurrency(lineTotal)}</td>
                    <td>{order.createdBy || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SalesOrders;
